/**
 * Sync handlers for the MAIN app's durable Site Survey write path.
 *
 * The SyncEngine calls one handler per queued mutation type. Non-photo writes
 * are a thin pass-through to the existing `survey_*` callable. Photo writes
 * upload the durable blob via the signed-URL transport (reusing the same
 * contract as the online path), record it via the callable, then evict the blob
 * from PhotoStore — precisely by its own blobKey, only AFTER a durable upload
 * (see surveyMutations.ts for why the SDK's own eviction is disabled).
 *
 * Error classification (audit A4): PERMISSION_DENIED / INVALID_ARGUMENT are
 * permanent (do not retry). UNAUTHENTICATED is deliberately transient — after a
 * long offline stretch the first reconnect often carries an expired token,
 * exactly when a retry after re-auth succeeds.
 *
 * PHASE-4 GATE (photos): the `addPhoto` / `addIntakePhoto` handlers below are
 * tested Phase-4 scaffolding and are NOT yet enqueued by any hook. Photo capture
 * currently uses the INLINE upload path (useSurveyPhotos / useSurveyIntake)
 * because the server mints the photoId inside getUploadUrl, so a queued replay
 * would duplicate the photo. Phase 4 makes the durable photo path safe via
 * server idempotency (client-minted photoId + server dedup); only then should
 * the capture hooks route photos through `enqueueSurveyMutation({ photos })`.
 */
import { httpsCallable } from '../firebase/authReadyCallable';
import { staleWriteError } from '@officehub/offline-sdk';
import type { SyncHandler, SyncHandlerResult, PhotoStore } from '@officehub/offline-sdk';
import { functions } from '../../config/firebase';
import { getSurveyUploadUrl, uploadBlobToSignedUrlWithRetry } from '../surveyPhotoUpload';
import { SURVEY_MUTATION } from './surveyMutationTypes';
import { storedSurveyPhotoUrl } from './storedSurveyPhotoUrl';

// Firebase httpsCallable rejects with a FunctionsError carrying a `.code` like
// 'functions/permission-denied'; the enum text never appears in `.message`, so
// classify on the CODE. Only auth-independent client errors are permanent —
// 'functions/unauthenticated' stays retryable (audit A4): the first reconnect
// after a long offline stretch often carries an expired token that a re-auth +
// retry clears. Classifying on message substrings made everything permanent-or-
// retryable by accident; this is the fix.
const PERMANENT_ERROR_CODES = new Set([
  'functions/permission-denied',
  'functions/invalid-argument',
]);

// Fallback for non-FunctionsError shapes (a plain Error thrown off the callable
// path) that carry the enum text in the message instead of a `.code`.
const PERMANENT_MESSAGE_TOKENS = ['PERMISSION_DENIED', 'INVALID_ARGUMENT'];

function isPermanentError(err: unknown): boolean {
  const code = (err as { code?: unknown } | null)?.code;
  if (typeof code === 'string') {
    return PERMANENT_ERROR_CODES.has(code);
  }
  const message = err instanceof Error ? err.message : String(err);
  return PERMANENT_MESSAGE_TOKENS.some((token) => message.includes(token));
}

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Unknown error';
}

const NO_REASONS: ReadonlySet<string> = new Set();

/** The server's `details.reason` on a refusal (FunctionsError carries the HttpsError details). */
function refusalReason(err: unknown): string | null {
  const details = (err as { details?: unknown } | null)?.details;
  const reason = details && typeof details === 'object' ? (details as { reason?: unknown }).reason : undefined;
  return typeof reason === 'string' ? reason : null;
}

/** The refusal reason for a save whose base map another device has since changed. */
const STALE_WRITE_REASON = 'stale-write';

/**
 * survey_saveMap refuses with failed-precondition for two reasons a retry of the SAME
 * payload can never fix: the map changed on another device since this edit
 * (stale-write), or a placement option still stands on a pin or route the save
 * removes (placement-option-in-use). Matched by the server's details.reason, NOT by
 * the code alone, so any other failed-precondition (not audited for transient causes)
 * keeps its normal retries. Scoped to this callable: others are not audited either.
 */
const SAVE_MAP_PERMANENT_REASONS: ReadonlySet<string> = new Set([STALE_WRITE_REASON, 'placement-option-in-use']);

/**
 * Map a thrown error to a handler result; UNAUTHENTICATED stays retryable (A4).
 * A failed-precondition whose reason is in `permanentReasons` is final. A stale-write
 * carries a marker so the failed-items panel can say what happened and not offer a
 * Retry that can only be refused again.
 */
function classifyError(err: unknown, permanentReasons: ReadonlySet<string> = NO_REASONS): SyncHandlerResult {
  const code = (err as { code?: unknown } | null)?.code;
  const reason = refusalReason(err);
  if (code === 'functions/failed-precondition' && reason !== null && permanentReasons.has(reason)) {
    const message = errorMessage(err);
    return { ok: false, retryable: false, error: reason === STALE_WRITE_REASON ? staleWriteError(message) : message };
  }
  return { ok: false, retryable: !isPermanentError(err), error: errorMessage(err) };
}

/** Pass-through handler: invoke `survey_<name>` with the mutation payload as-is. */
function callableHandler(name: string, permanentReasons: ReadonlySet<string> = NO_REASONS): SyncHandler {
  return async (mutation): Promise<SyncHandlerResult> => {
    try {
      const call = httpsCallable(functions, name);
      await call(mutation.payload as Record<string, unknown>);
      return { ok: true, retryable: false };
    } catch (err: unknown) {
      return classifyError(err, permanentReasons);
    }
  };
}

/** Shown when the server predates placement options (the map was saved, the options were not). */
export const SERVER_WITHOUT_PLACEMENT =
  'The map was saved, but the placement options were not saved: the server has not been updated for them yet. Keep this page open and tell the office.';

/**
 * survey_saveMap. A server from before survey PR 5c ignores `placement`: it saves the map and
 * answers success with no placementWriteVersion. The options were NOT saved, so that answer is a
 * permanent, visible failure, never "saved". Mirrors sales-client/src/lib/surveyMapSync.ts.
 */
function saveMapHandler(): SyncHandler {
  return async (mutation): Promise<SyncHandlerResult> => {
    let res: { data: unknown };
    try {
      res = await httpsCallable(functions, 'survey_saveMap')(mutation.payload as Record<string, unknown>);
    } catch (err: unknown) {
      return classifyError(err, SAVE_MAP_PERMANENT_REASONS);
    }
    const sentPlacement = (mutation.payload as { placement?: unknown } | null)?.placement != null;
    const placementVersion = (res.data as { placementWriteVersion?: unknown } | null)?.placementWriteVersion;
    if (sentPlacement && typeof placementVersion !== 'number') {
      return { ok: false, retryable: false, error: SERVER_WITHOUT_PLACEMENT };
    }
    return { ok: true, retryable: false };
  };
}

interface UploadedPhoto {
  readonly photoId: string;
  readonly storageRef: string;
  readonly url: string;
}

/** Upload one durable blob to Storage via a signed URL; return its recorded refs. */
async function uploadSurveyBlob(surveyId: string, blob: Blob): Promise<UploadedPhoto> {
  const contentType = blob.type || 'image/jpeg';
  const { uploadUrl, storageRef, photoId } = await getSurveyUploadUrl(surveyId, contentType);
  await uploadBlobToSignedUrlWithRetry(uploadUrl, blob, contentType);
  return { photoId, storageRef, url: storedSurveyPhotoUrl(storageRef) };
}

/**
 * Photo handler: upload each durable blob, record it via the callable, then
 * evict that blob by its exact blobKey. Per-blob eviction keeps a multi-blob
 * mutation replay-safe — an already-uploaded blob is gone from PhotoStore, so a
 * replay only re-processes the blobs that did not complete.
 *
 * `withSection` distinguishes section photos (`survey_addPhoto`, carries
 * `section`/`tag`/`caption`) from intake photos (`survey_addIntakePhoto`).
 */
function photoHandler(
  photoStore: PhotoStore,
  callableName: string,
  withSection: boolean,
): SyncHandler {
  return async (mutation, blobs): Promise<SyncHandlerResult> => {
    try {
      const payload = mutation.payload as Record<string, unknown>;
      const surveyId = payload.surveyId as string;

      // Materialize the blob map without relying on Map iteration (keeps the
      // transpile target-agnostic — no downlevelIteration requirement).
      const entries: Array<[string, Blob]> = [];
      blobs.forEach((blob, blobKey) => entries.push([blobKey, blob]));

      for (let i = 0; i < entries.length; i += 1) {
        const blobKey = entries[i][0];
        const blob = entries[i][1];
        const uploaded = await uploadSurveyBlob(surveyId, blob);
        const photo: Record<string, unknown> = {
          photoId: uploaded.photoId,
          url: uploaded.url,
          storageRef: uploaded.storageRef,
        };
        if (withSection) {
          photo.tag = payload.tag;
          if (payload.caption !== undefined) photo.caption = payload.caption;
        }
        const call = httpsCallable(functions, callableName);
        await call({
          organizationId: payload.organizationId,
          surveyId,
          ...(withSection ? { section: payload.section } : {}),
          photo,
        });
        // Durable in Storage + recorded — safe to evict this blob now.
        await photoStore.deleteBlob(blobKey);
      }

      return { ok: true, retryable: false };
    } catch (err: unknown) {
      return classifyError(err);
    }
  };
}

/**
 * Build the mutation-type → handler registry. `photoStore` is injected (rather
 * than imported) to avoid an import cycle with offlineEngine.ts.
 */
export function getSurveySyncHandlers(deps: {
  photoStore: PhotoStore;
}): Record<string, SyncHandler> {
  const { photoStore } = deps;
  return {
    [SURVEY_MUTATION.create]: callableHandler('survey_createSurvey'),
    [SURVEY_MUTATION.updateSection]: callableHandler('survey_updateSection'),
    [SURVEY_MUTATION.updateNoteCards]: callableHandler('survey_updateNoteCards'),
    [SURVEY_MUTATION.markSectionComplete]: callableHandler('survey_markSectionComplete'),
    [SURVEY_MUTATION.updateStatus]: callableHandler('survey_updateStatus'),
    [SURVEY_MUTATION.setLocation]: callableHandler('survey_setLocation'),
    [SURVEY_MUTATION.saveMap]: saveMapHandler(),
    [SURVEY_MUTATION.addContact]: callableHandler('survey_addContact'),
    [SURVEY_MUTATION.removePhoto]: callableHandler('survey_removePhoto'),
    [SURVEY_MUTATION.addPhoto]: photoHandler(photoStore, 'survey_addPhoto', true),
    [SURVEY_MUTATION.addIntakePhoto]: photoHandler(photoStore, 'survey_addIntakePhoto', false),
  };
}
